import { ReportContext } from "@reporting/rp-types/analytics";
import { AnalyticsApplication } from "..";
import { ReportResult } from "./reportResult";
import { CsvArrayStream } from "@reporting/rp-utils-csv";
import { ResultSpecs } from "../types";
import { utils } from "@omegagrid/core";
import { TransactionResult } from "./transactionResult";
import { RequestParams } from "@reporting/rp-app-host";

const DEFAULT_REQUEST_PARAMS: RequestParams = {
	history: 1,	// save request in history
	csvParams: {
		delimiter: '\t',
		header: true,
		headerJson: true,
		nullValue: ''
	},
};

export class ReportBroker {

	private _reportData = new Map<number, ReportResult>();

	constructor(private app: AnalyticsApplication) { }

	get client() { return this.app.container.ms('analytics') }

	private async readResultStream(response: Response, cb: (resultSpec: ResultSpecs, data: string[][]) => void) : Promise<void> {
		const reader = response.body.pipeThrough(new CsvArrayStream({ quote: '', delimiter: '\t', nullValue: 'NULL' })).getReader();
		let done = false, datasetEnd = false, data: string[][] = null, resultSpec: ResultSpecs = null;
		while (!done) {
			const result = await reader.read();
			done = result.done;
			if ((!result.value) || (result.value.length == 1 && result.value[0] === '\x1E')) {
				datasetEnd = true;
			} else if (!resultSpec) {
				datasetEnd = false;
				resultSpec = null;
				try { resultSpec = JSON.parse(result.value[0]) } catch (error) {
					resultSpec = null;
				}
				if (resultSpec && resultSpec.errors?.length) {
					throw new Error(resultSpec.errors.map(e => e.error).join('; '));
				}
				data = [];
			} else {
				data.push(result.value);
			}

			if (datasetEnd && data) {
				resultSpec.tableIndex = resultSpec.table_index;
				resultSpec.tableType = resultSpec.table_type;
				resultSpec.axisIndex = resultSpec.axis_index;
				resultSpec.axisLevel = resultSpec.axis_level;
				resultSpec.axisType = resultSpec.axis_type;
				resultSpec.dataColumn = resultSpec.data_column;
				resultSpec.emptyItems = resultSpec.empty_items;
				cb(resultSpec, data);
				datasetEnd = false;
				resultSpec = null;
				data = null;
			}
		}
	}

	async getTransactionData(context: Partial<ReportContext>|number, params?: RequestParams) : Promise<TransactionResult> {
		params = utils.mergeDeep({}, DEFAULT_REQUEST_PARAMS, params);
		const idContext: number = utils.isObject(context) ? (context as ReportContext).id_context : context as number;
		const response = idContext
			? await this.client.getRaw(`report/${idContext}`, params)
			: await this.client.postRaw(`report`, (context as ReportContext).context, params);

		const result = new TransactionResult();
		await this.readResultStream(response, (resultSpec, data) => result.addResultSet(resultSpec, data));
		return result;
	}

	async getReportData(context: Partial<ReportContext>|number, options?: {
		parentContext?: ReportContext,
		history?: boolean
	}) : Promise<ReportResult> {
		const idContext: number = utils.isObject(context) ? (context as ReportContext).id_context : context as number;

		const params = Object.assign({}, DEFAULT_REQUEST_PARAMS);
		params.history = options?.history === false ? 0 : 1;
		if (options?.parentContext) {
			params.module = options.parentContext.module;
			params.id_context = options.parentContext.id_context;
		}

		const response = idContext
			? await this.client.getRaw(`report/${idContext}`, params)
			: await this.client.postRaw(`report`, (context as ReportContext).context, params);

		if (!response.ok) {
			let message: string;
			try {
				const result = await response.json();
				message = result?.message;
			} catch (error) { }

			message = message || `${response.status} ${response.statusText}`;

			if (response.status === 403) {
				throw new Error(`Access denied: ${message}`);
			} else {
				throw new Error(`Error fetching report data: ${message}`);
			}
		}

		const result = new ReportResult();
		await this.readResultStream(response, (resultSpec, data) => result.addResultSet(resultSpec, data));
		return result;

		// TODO: implement better caching
		// TODO: must be cached by idContext and accounting

		// if (!this._reportData.has(idContext)) {
		// 	this._reportData.set(idContext, await this.queryReport(idContext));
		// }
		// // console.log(this._reportData.get(idContext));
		// return this._reportData.get(idContext);
	}
}
